import React, { Fragment } from 'react';

import cx from 'classnames';
import MediaQuery from 'react-responsive';

import Render from 'components/render';
import { device } from 'helpers/screenSizes';
import { AngleRight, TickIcon } from 'styles/assets/icons';
import { Box } from 'UIKit';

import styles from './steps.module.scss';

interface Level {
  id: number;
  text?: string;
  mobileText?: string;
}

interface StepProps {
  levels: Level[];
  activeLevel: number;
  chainStyles?: string;
}

interface LevelIconProps {
  activeLevel: number;
  currentLevel: number;
}

const LevelIcon = ({ activeLevel, currentLevel }: LevelIconProps) => {
  if (activeLevel === currentLevel) {
    return (
      <Box className={styles.activeLevel}>
        <Box className={styles.outerCircle}>
          <Box className={styles.innerCircle} />
        </Box>
      </Box>
    );
  }

  if (currentLevel < activeLevel) {
    return (
      <Box className={styles.completedLevel}>
        <Box className={styles.outerCircle}>
          <TickIcon color="var(--main-bg-primary)" />
        </Box>
      </Box>
    );
  }
  return <Box className={styles.inactiveLevel} />;
};

const StepsMobile = ({
  activeLevel,
  levels,
}: Pick<StepProps, 'activeLevel' | 'levels'>) => {
  return (
    <Box className={styles.stepsMobile}>
      {levels.map(({ id, mobileText }, idx) => {
        const lastIndex = idx === levels.length - 1;
        return (
          <Fragment key={id}>
            <Box
              as="span"
              textColor={activeLevel === idx ? 'primaryTheme' : 'primary'}
              className={styles.text}
            >
              {mobileText}
            </Box>
            <Render when={!lastIndex}>
              <AngleRight color="var(--main-text-tertiary)" />
            </Render>
          </Fragment>
        );
      })}
    </Box>
  );
};

const Steps = ({ activeLevel, levels, chainStyles }: StepProps) => {
  return (
    <>
      <MediaQuery query={device.up.sm}>
        <Box flex="flex" flexDir="column" className={styles.stepsContainer}>
          {levels.map(({ id, text }, idx) => {
            const lastIndex = idx === levels.length - 1;
            return (
              <Fragment key={id}>
                <Box flex="inline" gap={8} alignItems="center" key={id}>
                  <LevelIcon activeLevel={activeLevel} currentLevel={idx} />
                  <Box fontSize="sm4" renderWhen={Boolean(text)} textColor="secondary">
                    {text}
                  </Box>
                </Box>
                <Box
                  flex="inline"
                  justifyContent="center"
                  className={styles.connectingLinesBox}
                >
                  <Box
                    as="hr"
                    renderWhen={!lastIndex}
                    className={cx(styles.connectingLines, chainStyles)}
                  />
                </Box>
              </Fragment>
            );
          })}
        </Box>
      </MediaQuery>
      <MediaQuery query={device.down.sm}>
        <StepsMobile activeLevel={activeLevel} levels={levels} />
      </MediaQuery>
    </>
  );
};

export default Steps;
